import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, Smartphone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { isIOS, isStandalone, pushSupported, urlB64ToUint8 } from "@/lib/device";

const deviceName = () => (isIOS() ? "iPhone" : /android/i.test(navigator.userAgent) ? "Android" : "Browser");

const useThisDevice = () => {
  const [sub, setSub] = useState(undefined);
  useEffect(() => {
    if (!pushSupported()) { setSub(null); return undefined; }
    navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then((s) => setSub(s ?? null)).catch(() => setSub(null));
    return undefined;
  }, []);
  return [sub, setSub];
};

const Hint = ({ children }) => <p data-testid="push-hint" className="mt-4 rounded-xl bg-sky-50 border border-sky-100 p-3 text-sm text-slate-700">{children}</p>;

export const NotifyCard = () => {
  const [sub, setSub] = useThisDevice();
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadDevices = () => api.get("/push/status").then((r) => setDevices(Array.isArray(r.data?.devices) ? r.data.devices : [])).catch(() => setDevices([]));
  useEffect(() => { loadDevices(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") throw new Error("Notifications were blocked. Allow them in Settings → Notifications → Daybreyk.");
      const { data } = await api.get("/push/key");
      const reg = await navigator.serviceWorker.ready;
      const s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(data.key) });
      await api.post("/push/subscribe", { ...s.toJSON(), device: deviceName() });
      setSub(s); loadDevices();
      toast.success("Notifications on for this device");
    } catch (e) { toast.error(e?.response ? formatApiError(e) : e.message); }
    setBusy(false);
  };
  const disable = async () => {
    setBusy(true);
    try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); setSub(null); loadDevices(); toast.success("Notifications off for this device"); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const test = async () => {
    try { await api.post("/push/test"); toast.success("Test notification sent"); } catch (e) { toast.error(formatApiError(e)); }
  };

  const needsInstall = isIOS() && !isStandalone();
  return (
    <article data-testid="push-card" className="rounded-2xl bg-surface border border-slate-200 p-5 sm:p-6 lg:col-span-2">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-amber-50 text-amber-600"><BellRing className="w-5 h-5" /></span>
        <div><h3 className="font-semibold text-slate-900">Phone notifications</h3><p className="text-sm text-slate-500">A buzz at 07:00 when your brief is ready, and at 19:00 with tomorrow's kit (Sun–Thu).</p></div>
      </div>
      {needsInstall ? <Hint>On iPhone, add Daybreyk to your Home Screen first (Safari → Share → Add to Home Screen), then open it from there and come back to this page.</Hint>
        : !pushSupported() ? <Hint>This browser can't receive notifications. Try Safari on iPhone (iOS 16.4+) from the Home Screen app, or Chrome.</Hint>
        : (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <p data-testid="push-status" className="flex-1 text-sm text-slate-700">{sub === undefined ? "Checking…" : sub ? "On for this device." : "Off for this device."}</p>
            {sub && <button data-testid="push-test-button" onClick={test} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Send test</button>}
            <button data-testid="push-toggle-button" disabled={busy || sub === undefined} onClick={sub ? disable : enable}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-black hover:bg-slate-700 disabled:opacity-40 transition-colors">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}{sub ? "Turn off" : "Turn on notifications"}
            </button>
          </div>
        )}
      {devices.length > 0 && (
        <p data-testid="push-devices" className="mt-3 flex items-center gap-1.5 text-xs text-slate-500"><Smartphone className="w-3.5 h-3.5" />{devices.length} device{devices.length > 1 ? "s" : ""} signed up: {devices.map((d) => d.device || "Device").join(", ")}</p>
      )}
    </article>
  );
};
